// Sign-in: an email address and a password, for somebody an operator onboarded.
//
// There is no "create an account" link here and there must not be one. This is
// agency-onboarded software: an operator creates the office and invites each
// person (packages/db/cli.ts), and the person arrives with a single-use link
// that lands on this same screen in its set-password mode. Everything else is
// an existing account signing in.
//
// The server decides everything that matters. This screen sends what the
// person typed to POST /v1/auth/sign-in and does what the answer says:
//
//   * 200 — a session token, handed to `onToken`, which still checks it against
//     GET /me before anything opens. The app holds no credential of its own.
//   * 422 with `totpCode: required` — the password was right and this account
//     has a second factor. The code field appears; the password stays filled.
//   * 401 — one sentence for every refusal, because the API gives one answer
//     for an unknown address, a wrong password and a wrong code, and a screen
//     that guessed which it was would be undoing that.
//   * 429 — how long to wait, from Retry-After as the API stated it.
//
// The invitation token arrives in the URL FRAGMENT (`#invitation=…`), which a
// browser never sends to a server. It is removed from the address bar as soon
// as it is read, so it does not survive in history or a screenshot.

import { useEffect, useState, type ReactNode } from 'react';
import { ApiError, publicRequest } from '../api/client.ts';
import type { SessionGrant } from '../api/types.ts';

export interface SignInProps {
  readonly onToken: (token: string) => void;
  readonly error: string | null;
  readonly busy: boolean;
}

/** Read and REMOVE the invitation token from the fragment. */
export function takeInvitationFromLocation(): string | null {
  try {
    const match = /(?:^#|&)invitation=([A-Za-z0-9_-]{32,256})(?:&|$)/.exec(window.location.hash);
    if (match === null) return null;
    window.history.replaceState(null, '', window.location.pathname + window.location.search);
    return match[1] ?? null;
  } catch {
    return null;
  }
}

function refusalText(error: unknown): string {
  if (error instanceof ApiError) {
    if (error.code === 'transport_error') {
      return 'The service could not be reached. Check your connection and try again.';
    }
    if (error.status === 429) {
      return 'Too many attempts. Wait a few minutes before trying again.';
    }
    if (error.status === 401) return 'That email address and password do not match an account.';
  }
  return 'Sign-in failed. Try again.';
}

export function SignIn({ onToken, error, busy }: SignInProps): ReactNode {
  const [invitation, setInvitation] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  useEffect(() => {
    setInvitation(takeInvitationFromLocation());
  }, []);

  return (
    <div className="signin">
      <div className="card">
        <div className="card-head">
          <div>
            <h2>AI Bookkeeper</h2>
            <p>{invitation === null ? 'Sign in to your office workspace.' : 'Choose your password.'}</p>
          </div>
        </div>
        {invitation === null ? (
          <CredentialForm onToken={onToken} outerError={error} outerBusy={busy} notice={notice} />
        ) : (
          <SetPasswordForm
            invitation={invitation}
            onDone={() => {
              setInvitation(null);
              setNotice('Your password is set. Sign in with it now.');
            }}
          />
        )}
      </div>
    </div>
  );
}

function CredentialForm({
  onToken,
  outerError,
  outerBusy,
  notice,
}: {
  readonly onToken: (token: string) => void;
  readonly outerError: string | null;
  readonly outerBusy: boolean;
  readonly notice: string | null;
}): ReactNode {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [needsCode, setNeedsCode] = useState(false);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const busy = pending || outerBusy;
  const ready =
    email.trim() !== '' && password !== '' && (!needsCode || /^[0-9]{6}$/.test(code));
  const shown = error ?? outerError;

  async function submit(): Promise<void> {
    setPending(true);
    setError(null);
    try {
      const grant = await publicRequest<SessionGrant>('/v1/auth/sign-in', {
        email: email.trim(),
        password,
        ...(needsCode ? { totpCode: code } : {}),
      });
      setPassword('');
      setCode('');
      onToken(grant.token);
    } catch (caught) {
      if (
        caught instanceof ApiError &&
        caught.status === 422 &&
        caught.fieldErrors.some((f) => f.field === 'totpCode' && f.code === 'required')
      ) {
        setNeedsCode(true);
      } else {
        setCode('');
        setError(refusalText(caught));
      }
    } finally {
      setPending(false);
    }
  }

  return (
    <form
      className="card-body"
      onSubmit={(event) => {
        event.preventDefault();
        if (ready && !busy) void submit();
      }}
    >
      {notice === null || shown !== null ? null : (
        <p className="muted" role="status" style={{ marginBottom: 16 }}>
          {notice}
        </p>
      )}
      {shown === null ? null : (
        <p className="error-banner" role="alert">
          {shown}
        </p>
      )}
      <label className="field">
        Email address
        <input
          type="email"
          autoComplete="username"
          value={email}
          onChange={(event) => setEmail(event.target.value)}
          disabled={needsCode}
          required
        />
      </label>
      <label className="field">
        Password
        <input
          type="password"
          autoComplete="current-password"
          value={password}
          onChange={(event) => setPassword(event.target.value)}
          disabled={needsCode}
          required
        />
      </label>
      {needsCode ? (
        <label className="field">
          Authentication code
          <input
            inputMode="numeric"
            autoComplete="one-time-code"
            pattern="[0-9]{6}"
            maxLength={6}
            value={code}
            onChange={(event) => setCode(event.target.value.replace(/\D/g, ''))}
            aria-describedby="code-help"
            autoFocus
          />
          <span id="code-help" className="muted small">
            The six-digit code from your authenticator app.
          </span>
        </label>
      ) : null}
      <button type="submit" className="btn primary" disabled={busy || !ready}>
        {busy ? 'Signing in…' : needsCode ? 'Verify and sign in' : 'Sign in'}
      </button>
      <p className="muted small" style={{ marginTop: 16 }}>
        Accounts are created by your bookkeeping office. If you have not received an invitation, or
        have lost your password or authentication device, ask them to send a new one.
      </p>
    </form>
  );
}

const POLICY_TEXT: Readonly<Record<string, string>> = {
  too_short: 'Use at least 12 characters. A short sentence works well.',
  too_long: 'Use at most 128 characters.',
  is_email: 'Your password cannot be your email address.',
};

function SetPasswordForm({
  invitation,
  onDone,
}: {
  readonly invitation: string;
  readonly onDone: () => void;
}): ReactNode {
  const [password, setPassword] = useState('');
  const [repeat, setRepeat] = useState('');
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const length = [...password].length;
  const mismatch = repeat !== '' && repeat !== password;
  const ready = length >= 12 && repeat === password;

  async function submit(): Promise<void> {
    setPending(true);
    setError(null);
    try {
      await publicRequest<{ passwordSet: true }>('/v1/auth/set-password', { invitation, password });
      onDone();
    } catch (caught) {
      if (caught instanceof ApiError && caught.status === 422) {
        const rule = caught.fieldErrors.find((f) => f.field === 'password')?.code;
        setError((rule === undefined ? undefined : POLICY_TEXT[rule]) ?? 'That password cannot be used.');
      } else if (caught instanceof ApiError && caught.status === 401) {
        setError(
          'This invitation link has expired or has already been used. Ask your bookkeeping office for a new one.',
        );
      } else {
        setError(refusalText(caught));
      }
    } finally {
      setPending(false);
    }
  }

  return (
    <form
      className="card-body"
      onSubmit={(event) => {
        event.preventDefault();
        if (ready && !pending) void submit();
      }}
    >
      {error === null ? null : (
        <p className="error-banner" role="alert">
          {error}
        </p>
      )}
      <label className="field">
        New password
        <input
          type="password"
          autoComplete="new-password"
          value={password}
          onChange={(event) => setPassword(event.target.value)}
          aria-describedby="password-help"
          required
        />
        <span id="password-help" className="muted small">
          At least 12 characters. No other rules — a sentence you will remember is a good choice.
        </span>
      </label>
      <label className="field">
        Repeat the password
        <input
          type="password"
          autoComplete="new-password"
          value={repeat}
          onChange={(event) => setRepeat(event.target.value)}
          aria-invalid={mismatch}
          required
        />
        {mismatch ? <span className="muted small">The two passwords are not the same.</span> : null}
      </label>
      <button type="submit" className="btn primary" disabled={pending || !ready}>
        {pending ? 'Saving…' : 'Set password'}
      </button>
    </form>
  );
}
